import { FC, useMemo, useState } from "react";
import LinkIcon from "@src/lib/icons/18px/link";
import { Skeleton } from "@src/lib/ui/Skeleton";
import { cn } from "@src/lib/utils/cn";

type Props = {
  url: string;
  loading: boolean;
  ogData: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  maxUnfurledWidth?: number;
  /** Fill the container instead of the standard 256px card. */
  fillWidth?: boolean;
};

// OpenGraph fields come back as JSON; an absent tag is `null`, not missing.
const ogString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const SAFE_SRC_PROTOCOLS = new Set(["http:", "https:"]);
const SAFE_HREF_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/**
 * A stored link as something safe to put in an href. Scheme-less links
 * ("www.google.com", "localhost:3000/x") get https — as an href they would
 * otherwise be a path on this site — and anything that isn't a web, mail or
 * phone link (javascript:, data:…) is refused.
 */
export const toSafeHref = (url: string): string | null => {
  const trimmed = url.trim();
  const hasScheme =
    /^[a-z][a-z\d+.-]*:/i.test(trimmed) &&
    // "host:port" reads like a scheme but isn't one.
    !/^[^/:?#]+:\d+(?:[/?#]|$)/.test(trimmed);
  const candidate = trimmed.startsWith("//")
    ? `https:${trimmed}`
    : hasScheme
      ? trimmed
      : `https://${trimmed}`;
  try {
    const parsed = new URL(candidate);
    return SAFE_HREF_PROTOCOLS.has(parsed.protocol) ? candidate : null;
  } catch {
    return null;
  }
};

/**
 * A page-supplied media URL (og:image, og:video) resolved against the page
 * and kept only if it is plain http(s) — never javascript: or data: in an
 * iframe or image.
 */
const toSafeSrc = (
  value: string | null,
  base: string | null,
): string | null => {
  if (!value) return null;
  try {
    const parsed = base ? new URL(value, base) : new URL(value);
    return SAFE_SRC_PROTOCOLS.has(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

/**
 * A link as a card: the page's preview image when it publishes one, then its
 * title and domain. A page without an image still reads as a link — title,
 * domain and a link glyph — rather than a bare URL.
 */
const UnfurledMedia: FC<Props> = ({
  url,
  ogData,
  loading,
  maxUnfurledWidth,
  fillWidth = false,
}) => {
  const href = useMemo(() => toSafeHref(url), [url]);
  const hostname = useMemo(() => {
    if (!href) return url;
    try {
      return new URL(href).hostname.replace(/^www\./, "") || url;
    } catch {
      return url;
    }
  }, [href, url]);
  // Remembered per image, so a replaced link's new image gets its chance.
  const [failedImage, setFailedImage] = useState<string | null>(null);
  // Filling a container, the card takes its container's rounder corners so
  // a clipping parent doesn't shave the border.
  const cardShape = cn(
    "flex flex-col overflow-hidden border border-border-primary bg-bg-primary",
    fillWidth ? "rounded-2xl" : "rounded-xl",
  );
  const width = fillWidth
    ? "100%"
    : maxUnfurledWidth !== undefined
      ? Math.min(maxUnfurledWidth, 256)
      : 256;

  if (loading) {
    return (
      <div className={cardShape} style={{ width }}>
        <Skeleton className="aspect-[1.91/1] w-full rounded-none" />
        <div className="flex flex-col gap-1.5 p-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    );
  }

  const ogTitle = ogString(ogData?.["og:title"]);
  const pageUrl = toSafeSrc(ogString(ogData?.["og:url"]), href) ?? href;
  const videoUrl = toSafeSrc(ogString(ogData?.["og:video:url"]), pageUrl);
  const image = toSafeSrc(ogString(ogData?.["og:image"]), pageUrl);
  const showImage = Boolean(image) && image !== failedImage;
  const title = ogTitle ?? hostname;

  const caption = (
    <div className="flex min-w-0 flex-col gap-0.5 px-3 py-2.5">
      <span className="line-clamp-2 text-s-v4 font-medium-v4 text-text-primary">
        {title}
      </span>
      <span className="flex min-w-0 items-center gap-1 text-xs-v4 text-text-tertiary">
        <LinkIcon className="size-3 shrink-0" />
        <span className="truncate">{hostname}</span>
      </span>
    </div>
  );

  if (videoUrl) {
    // The page's own proportions when it states them; 16:9 otherwise.
    const ogWidth = Number(ogData?.["og:video:width"]);
    const ogHeight = Number(ogData?.["og:video:height"]);
    const aspectRatio =
      ogWidth > 0 && ogHeight > 0 ? ogWidth / ogHeight : 16 / 9;
    // A plain video file plays in a video element, with the page's image as
    // its poster; anything else is an embed player and keeps its iframe.
    const isVideoFile =
      ogString(ogData?.["og:video:type"])?.startsWith("video/") ||
      /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(videoUrl);
    const mediaStyle = { aspectRatio };
    return (
      <div className={cardShape} style={{ width }}>
        {isVideoFile ? (
          <video
            className="w-full bg-bg-tertiary object-cover outline-none"
            style={mediaStyle}
            src={videoUrl}
            poster={showImage ? image ?? undefined : undefined}
            controls
            preload="metadata"
            playsInline
          />
        ) : (
          <iframe
            data-video-player
            className="w-full border-none outline-none"
            style={mediaStyle}
            title={title}
            src={videoUrl}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            allow="fullscreen; picture-in-picture"
          />
        )}
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {caption}
          </a>
        ) : (
          caption
        )}
      </div>
    );
  }

  const body = (
    <>
      {showImage ? (
        <img
          className="aspect-[1.91/1] w-full bg-bg-tertiary object-cover"
          src={image ?? undefined}
          alt=""
          onError={() => setFailedImage(image)}
        />
      ) : null}
      {caption}
    </>
  );
  // A link that can't be made safe still shows what it is, just not as a link.
  if (!href) {
    return (
      <div className={cardShape} style={{ width }}>
        {body}
      </div>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        cardShape,
        "no-underline transition-colors hover:bg-bg-secondary",
      )}
      style={{ width }}
    >
      {body}
    </a>
  );
};

export default UnfurledMedia;
